import React from 'react';
import { useTranslation } from 'react-i18next';

import { KeyPrefix } from 'i18next';

import HintButton from 'components/HintButton';
import { appReactMemo } from 'hocs';

import {
  Body,
  Container,
  ErrorMessage,
  Header,
  HintMessage,
  Title,
  TitleWrapper,
  Wrapper,
} from './styled';

interface IFormSection {
  children: React.ReactNode;
  className?: string;
  customComponent?: React.ReactNode;
  /**
   * Field path this section owns. When set, the section root exposes a
   * `data-error-field` anchor (and is made programmatically focusable) so
   * `useScrollToFirstError` can locate and focus section-level errors that have
   * no native `name` input — e.g. the merchants transfer list.
   */
  dataErrorField?: string;
  errorMsg?: string;
  headerSpaceBetween?: boolean;
  hintMsg?: string;
  hintText?: string;
  keyPrefix?: KeyPrefix<'translation'>;
  maxHintWidth?: number;
  sectionKey?: string;
}

const FormSection = ({
  children,
  className,
  customComponent,
  dataErrorField,
  errorMsg,
  headerSpaceBetween,
  hintMsg,
  hintText,
  keyPrefix,
  maxHintWidth,
  sectionKey,
}: IFormSection) => {
  const { t } = useTranslation('translation', { keyPrefix });

  const renderMessage = () => {
    if (errorMsg) {
      return <ErrorMessage>{`*${errorMsg}`}</ErrorMessage>;
    }
    if (hintMsg) {
      return <HintMessage>{`*${hintMsg}`}</HintMessage>;
    }
    return null;
  };

  return (
    <Container
      className={className}
      data-error-field={dataErrorField}
      id={sectionKey}
      tabIndex={dataErrorField ? -1 : undefined}
    >
      <Header $isSpaceBetween={headerSpaceBetween}>
        <Wrapper>
          <TitleWrapper>
            {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
            {/* @ts-ignore */}
            <Title as='h3'>{t(sectionKey)}</Title>
            {hintText && <HintButton maxHintWidth={maxHintWidth}>{hintText}</HintButton>}
          </TitleWrapper>
          {renderMessage()}
        </Wrapper>
        {customComponent}
      </Header>
      <Body>{children}</Body>
    </Container>
  );
};

export default appReactMemo(FormSection);
